import type { CSSProperties } from "react";
import type { StatusTint } from "@/lib/tokens";

interface DateChipProps {
  /** ISO date string, e.g. "2026-07-15" or a full datetime. */
  date: string;
  /** Optional month colour (see lib/memoListGroups `monthTint`). The look lives in
   *  index.css `.flow-month-chip`, which derives a light and a dark variant from the
   *  same hue vars `.flow-status-tint` uses — so no background/border inline here. */
  tint?: StatusTint;
}

const dayFmt = new Intl.DateTimeFormat("de-DE", { day: "numeric" });
const monthFmt = new Intl.DateTimeFormat("de-DE", { month: "short" });

/** 44x44 day/month chip for protocol-list rows ("15" over "JUL"). */
export function DateChip({ date, tint }: DateChipProps) {
  const d = new Date(date);
  const valid = !Number.isNaN(d.getTime());
  const day = valid ? dayFmt.format(d) : "–";
  const month = valid ? monthFmt.format(d) : "";

  const tinted: CSSProperties | undefined = tint
    ? ({ "--pill-bg": tint.bg, "--pill-fg": tint.fg, "--pill-dot": tint.dot } as CSSProperties)
    : undefined;

  return (
    <div
      className={tint ? "flow-month-chip" : undefined}
      data-testid="date-chip"
      style={{
        width: 44,
        height: 44,
        borderRadius: 10,
        ...(tint
          ? tinted
          : { background: "var(--surface-soft)", border: "1px solid var(--hairline)" }),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 16,
          fontWeight: 700,
          color: "var(--ink-1)",
          lineHeight: 1.15,
        }}
      >
        {day}
      </span>
      <span
        className={tint ? "flow-month-chip-label" : undefined}
        style={{
          fontSize: 10,
          fontWeight: tint ? 600 : 500,
          textTransform: "uppercase",
          letterSpacing: ".05em",
          ...(tint ? {} : { color: "var(--ink-3)" }),
          lineHeight: 1.2,
        }}
      >
        {month}
      </span>
    </div>
  );
}
